import { SelectCompanyRole } from "@components/role/select-company-role";
import { MemberRoleReadOnly } from "@components/table/members/members-column/role-column/member-role-read-only";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { MemberIdentityRoleProps } from "@custom-types/member-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { Props as FaroSelectProps } from "@components/common/faro-select/faro-select-utils";
import { useState } from "react";
import { FaroButtonSpinner } from "@components/common/button/faro-button-spinner";
import { Stack } from "@mui/material";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorkspaceMemberEvents } from "@utils/track-event/track-event-list";

interface Props extends BaseCompanyIdProps, MemberIdentityRoleProps {
  /** Forces rendering a read-only role label instead of a dropdown */
  isReadOnly?: boolean;

  /** Props to pass to the SelectCompanyRole component */
  variant: FaroSelectProps<string>["variant"];

  /** Props to pass to the SelectCompanyRole component */
  menuProps?: FaroSelectProps<string>["menuProps"];

  /** Location from which the member list is rendered, for tracking purposes. */
  location: "role-column" | "workspace-role-dropdown";
}

/**
 * Render and adjust member roles in company context.
 * This dropdown is showed to change the role for a single member inside a table.
 */
export function CompanyMemberRole({
  companyId,
  memberIdentityRole,
  isReadOnly = false,
  variant,
  menuProps,
  location,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const currentUser = useAppSelector(currentUserSelector);
  const { canEditMember } = useHasUserValidRoleCompanyLevel();

  const [isUpdating, setIsUpdating] = useState(false);

  const isCurrentUser = currentUser?.identity === memberIdentityRole.identity;

  const { changeMemberRole } = useChangeMemberRole({
    companyId,
    location,
    subjectType: "workspace",
    memberIdentity: memberIdentityRole.identity,
    currentRole: memberIdentityRole.role,
  });

  if (isReadOnly) {
    return (
      <MemberRoleReadOnly role={memberIdentityRole.role} variant={variant} />
    );
  }

  /**
   * Return non-editable member role if:
   * - Member is current logged user
   * - User has no permission to edit member
   */
  if (isCurrentUser || !canEditMember) {
    return (
      <MemberRoleReadOnly role={memberIdentityRole.role} variant={variant} />
    );
  }

  /** Trigger updating member role */
  async function onRoleChange(
    role: FaroCoreApiTypes.AssignmentRoleBySubjectType["company"]
  ): Promise<void> {
    trackEvent({
      name: WorkspaceMemberEvents.changeRole,
      props: { role },
    });

    if (isUpdating) {
      return;
    }

    try {
      setIsUpdating(true);
      await changeMemberRole(role);
    } catch {
      // Errors are already handled by the slice, only notify about a successful result
      return;
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <Stack direction="row">
      <SelectCompanyRole
        selectedRole={
          memberIdentityRole.role as FaroCoreApiTypes.AssignmentRoleBySubjectType["company"]
        }
        onChange={onRoleChange}
        variant={variant}
        menuProps={menuProps}
      />
      {isUpdating && <FaroButtonSpinner size="14px" />}
    </Stack>
  );
}
